import { useEffect, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

/**
 * Full-screen photo lightbox with keyboard + swipe controls.
 * Props:
 *  - images: Array<{ src, alt }>
 *  - index: number | null   (null = closed)
 *  - onClose: () => void
 *  - onChange: (nextIndex) => void
 */
export const Lightbox = ({ images, index, onClose, onChange }) => {
  const open = index !== null && index !== undefined;

  const next = useCallback(() => {
    if (!open) return;
    onChange((index + 1) % images.length);
  }, [open, index, images.length, onChange]);

  const prev = useCallback(() => {
    if (!open) return;
    onChange((index - 1 + images.length) % images.length);
  }, [open, index, images.length, onChange]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose, next, prev]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="lightbox"
          data-testid="lightbox"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="fixed inset-0 z-[300] flex items-center justify-center bg-[#052e1e]/95 backdrop-blur-xl"
          onClick={onClose}
        >
          {/* Close */}
          <button
            type="button"
            onClick={onClose}
            data-testid="lightbox-close"
            aria-label="Close"
            className="absolute top-5 right-5 z-[310] h-11 w-11 rounded-full border border-[#ddb128]/50 text-[#f7f3e9] flex items-center justify-center hover:bg-[#ddb128] hover:text-[#07412b] transition-colors"
          >
            <X size={20} />
          </button>

          {/* Prev */}
          <button
            type="button"
            data-testid="lightbox-prev"
            aria-label="Previous"
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            className="absolute left-3 sm:left-6 z-[310] h-11 w-11 sm:h-12 sm:w-12 rounded-full border border-[#ddb128]/40 text-[#f7f3e9] flex items-center justify-center hover:bg-[#ddb128] hover:text-[#07412b] transition-colors"
          >
            <ChevronLeft size={22} />
          </button>

          {/* Next */}
          <button
            type="button"
            data-testid="lightbox-next"
            aria-label="Next"
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            className="absolute right-3 sm:right-6 z-[310] h-11 w-11 sm:h-12 sm:w-12 rounded-full border border-[#ddb128]/40 text-[#f7f3e9] flex items-center justify-center hover:bg-[#ddb128] hover:text-[#07412b] transition-colors"
          >
            <ChevronRight size={22} />
          </button>

          {/* Image */}
          <AnimatePresence mode="wait">
            <motion.figure
              key={index}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.99 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.15}
              onDragEnd={(_, info) => {
                if (info.offset.x < -80) next();
                else if (info.offset.x > 80) prev();
              }}
              className="relative max-w-[92vw] max-h-[86vh] flex flex-col items-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={images[index]?.src}
                alt={images[index]?.alt}
                className="max-h-[80vh] max-w-[92vw] object-contain rounded-lg shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] select-none"
                draggable={false}
              />
              <figcaption className="font-script text-[#ddb128] text-2xl sm:text-3xl mt-4 text-center px-4">
                {images[index]?.alt}
              </figcaption>
              <p className="font-body text-[#f7f3e9]/50 text-[11px] tracking-[0.24em] uppercase mt-2">
                {index + 1} / {images.length}
              </p>
            </motion.figure>
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
